// pages/CreateUserPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { 
    Mail, 
    User, 
    Lock, 
    Phone,
    Shield,
    CheckCircle,
    AlertCircle
} from 'lucide-react';
import './CreateUserPage.css';
import DashboardBase  from "../DashboardBase";

const ROLES = [
    { value: 'ADMIN', label: 'Administrator' },
    { value: 'FUND_MANAGER', label: 'Fund Manager' }
];

const CreateUserPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        phone_number: '',
        role: 'FUND_MANAGER' // default value
    });

    const [validation, setValidation] = useState({
        email: true,
        first_name: true,
        last_name: true,
        password: true,
        phone_number: true
    });

    const validateForm = () => {
        const newValidation = {
            email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email),
            first_name: formData.first_name.length >= 2,
            last_name: formData.last_name.length >= 2,
            password: formData.password.length >= 8,
            phone_number: /^\+?[\d\s-]{10,}$/.test(formData.phone_number)
        };

        setValidation(newValidation);
        return Object.values(newValidation).every(Boolean);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            setError('Please correct the validation errors.');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/users`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create user');
            }

            setSuccess(true);
            setTimeout(() => {
                navigate('/users');
            }, 2000);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
     <DashboardBase
     page="home"
     content= { 
        <div className="create-user-page"> 
            <div className="dashboard-page">
            <div className="dashboard-header">
                <h1>Create New User</h1>
            </div>

            <div className="content-card">
                {success ? (
                    <div className="success-message">
                        <CheckCircle className="success-icon" />
                        <p>User created successfully! Redirecting...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="create-user-form">
                        {error && (
                            <div className="error-message">
                                <AlertCircle className="error-icon" />
                                {error}
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <div className="input-wrapper">
                                <Mail className="input-icon" />
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={!validation.email ? 'invalid' : ''}
                                    placeholder="Enter email address"
                                    required
                                />
                            </div>
                            {!validation.email && (
                                <span className="validation-message">
                                    Please enter a valid email address
                                </span>
                            )}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="first_name">First Name</label>
                                <div className="input-wrapper">
                                    <User className="input-icon" />
                                    <input
                                        type="text"
                                        id="first_name"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        className={!validation.first_name ? 'invalid' : ''}
                                        placeholder="Enter first name"
                                        required
                                    />
                                </div>
                                {!validation.first_name && (
                                    <span className="validation-message">
                                        First name must be at least 2 characters
                                    </span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="last_name">Last Name</label>
                                <div className="input-wrapper">
                                    <User className="input-icon" />
                                    <input
                                        type="text"
                                        id="last_name"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        className={!validation.last_name ? 'invalid' : ''}
                                        placeholder="Enter last name"
                                        required
                                    />
                                </div>
                                {!validation.last_name && (
                                    <span className="validation-message">
                                        Last name must be at least 2 characters
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <div className="input-wrapper">
                                <Lock className="input-icon" />
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={!validation.password ? 'invalid' : ''}
                                    placeholder="Enter password"
                                    required
                                />
                            </div>
                            {!validation.password && (
                                <span className="validation-message">
                                    Password must be at least 8 characters
                                </span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone_number">Phone Number</label>
                            <div className="input-wrapper">
                                <Phone className="input-icon" />
                                <input
                                    type="tel"
                                    id="phone_number"
                                    name="phone_number"
                                    value={formData.phone_number}
                                    onChange={handleChange}
                                    className={!validation.phone_number ? 'invalid' : ''}
                                    placeholder="Enter phone number"
                                    required
                                />
                            </div>
                            {!validation.phone_number && (
                                <span className="validation-message">
                                    Please enter a valid phone number
                                </span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="role">Role</label>
                            <div className="input-wrapper">
                                <Shield className="input-icon" />
                                <select
                                    id="role"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    required
                                >
                                    {ROLES.map(role => (
                                        <option key={role.value} value={role.value}>
                                            {role.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button 
                                type="button" 
                                className="button secondary"
                                onClick={() => navigate('/users')}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="button primary"
                                disabled={loading}
                            >
                                {loading ? 'Creating...' : 'Create User'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
        </div>
        }
        />
    );
};

export default CreateUserPage;